import { useState } from 'react';

import JxButton from '../../../JxControls/JxButtons/JxButton';
import JxToggle from '../../../JxControls/JxToggles/JxToggle.jsx';

import umlCanvasEngine from '../../Core/UmlCanvasEngine';
import umlConnectorEngine from '../../Core/UmlConnectorEngine';
import { detectImportLanguage } from '../../Core/Import/ImportLanguageDetector';
import { IMPORT_STRATEGIES, getImportStrategy } from '../../Core/Import/ImportStrategyRegistry';
import { findCanvasConnectors } from '../../Core/Import/CanvasConnectorFinder';

const STAGGER_OFFSET = 24;
const BASE_X = 80;
const BASE_Y = 80;

function nextPosition() {
    const n = umlCanvasEngine.shapes.length;
    return { x: BASE_X + (n % 8) * STAGGER_OFFSET, y: BASE_Y + (n % 8) * STAGGER_OFFSET };
}

/**
 * UmlSchemaImportTab — paste source in any supported language, auto-detects
 * which import strategy applies (ImportLanguageDetector), user can override
 * via the dropdown. Generate runs the selected strategy (ImportStrategyRegistry)
 * and adds the resulting shape to the canvas.
 * @param {{ onSave: () => void, isSaving: boolean, name: string }} props
 */
export default function UmlSchemaImportTab({ onSave, isSaving, name }) {
    const [source, setSource] = useState('');
    const [strategyKey, setStrategyKey] = useState('csharp');
    const [statusMsg, setStatusMsg] = useState(null);
    const [collapsed, setCollapsed] = useState(true);

    const handleSourceChange = (e) => {
        const value = e.target.value;
        setSource(value);
        if (value.trim()) {
            setStrategyKey(detectImportLanguage(value));
        }
    };

    const activeStrategy = getImportStrategy(strategyKey);

    /**
     * Generates shapes
     *
     * @returns
     */
    const handleGenerate = () => {
        if (!source.trim() || !activeStrategy?.run) {
            return;
        }
        const { x, y } = nextPosition();
        const result = activeStrategy.run(source, x, y, umlCanvasEngine.shapes, umlConnectorEngine.connectors, { collapsed });
        const shapes = result?.shapes ?? [];
        const connectors = result?.connectors ?? [];
        const warnings = result?.warnings ?? [];
        if (shapes.length === 0) {
            setStatusMsg('No types found to import');
            return;
        }
        shapes.forEach(function addEachShape(shape) {
            umlCanvasEngine.addShape(shape);
        });
        connectors.forEach(function addEachConnector(connector) {
            umlConnectorEngine.addConnector(connector);
        });
        const connectorText = connectors.length > 0 ? `, ${connectors.length} connector(s)` : '';
        const warningText = warnings.length > 0 ? ` (skipped: ${warnings.join('; ')})` : '';
        setStatusMsg(`Added ${shapes.length} shape(s)${connectorText} via ${activeStrategy.label}${warningText}`);
        setSource('');
    };

    // "Find Connectors" (2026-07-10) — retroactive, whole-canvas pass: no new
    // parsing, just re-scans EVERYTHING currently on the canvas
    // (umlCanvasEngine.shapes, including package children) for both
    // inheritance (interface/class base-lists) and constructor-dependency
    // associations that resolve to another shape by name.
    // see CanvasConnectorFinder.js
    const handleFindConnectors = () => {
        const connectors = findCanvasConnectors(umlCanvasEngine.shapes, umlConnectorEngine.connectors);
        connectors.forEach(function addEachConnector(connector) {
            umlConnectorEngine.addConnector(connector);
        });
        setStatusMsg(connectors.length > 0 ? `Added ${connectors.length} connector(s)` : 'No new connectors found');
    };

    return (
        <div className="usd-import">
            <div className="usd-import-text">Paste source — the language is auto-detected, override below if needed.</div>

            <select className="usd-import-select" value={strategyKey} onChange={(e) => setStrategyKey(e.target.value)}>
                {IMPORT_STRATEGIES.map(function renderOption(strategy) {
                    return (
                        <option key={strategy.key} value={strategy.key} disabled={!strategy.enabled}>
                            {strategy.label}
                        </option>
                    );
                })}
            </select>

            <textarea
                className="usd-textarea usd-import-textarea"
                value={source}
                onChange={handleSourceChange}
                placeholder={'public class Order\n{\n    public int Id { get; set; }\n    public void Submit() { }\n}'}
                spellCheck={false}
            />

            <div className="usd-button-row">
                <JxButton className="jx-button-sm" label="Save" onClick={onSave} disabled={!name?.trim() || isSaving} />

                <div className="usbt-button-spacer" />
                <JxToggle id="uml-import-collapsed-toggle" checked={collapsed} onChange={setCollapsed} label="Collapsed" />
                <JxButton className="jx-button-sm" label="Clear" onClick={() => setSource('')} />
                <JxButton className="jx-button-sm" label="Find Connectors" onClick={handleFindConnectors} />
                <JxButton
                    className="jx-button-sm"
                    label="Generate"
                    onClick={handleGenerate}
                    disabled={!source.trim() || !activeStrategy?.enabled}
                />
            </div>

            <div className="usd-status-bar">
                <span>{statusMsg ?? 'Ready — paste source and click Generate'}</span>
            </div>
        </div>
    );
}
